import {
  Box,
  Chip,
  CircularProgress,
  Typography,
  MenuItem,
  Select,
  FormControl,
  Checkbox,
  ListItemText,
} from "@material-ui/core";
import { useState, useEffect } from "react";
import YearBarChart from "./YearBarChart";
import useYearSeries from "../../../hooks/charts/useYearSeries";

export default function YearBarChartContainer({
  locationid,
  parameterid,
  initialActiveYears,
  showYearSelector = true,
  height = 400,
}) {
  const [depthFilter, setDepthFilter] = useState([]);
  const { series, years, depths, unit, isLoading, isError } = useYearSeries({
    locationid,
    parameterid,
    depthFilter,
  });

  const [activeYears, setActiveYears] = useState(initialActiveYears ?? []);

  useEffect(() => {
    if (!initialActiveYears && years.length && !activeYears.length) {
      setActiveYears(years.slice(-2));
    }
  }, [years, initialActiveYears, activeYears]);

  useEffect(() => {
    if (depths.length && !depthFilter.length) {
      setDepthFilter([depths[0]]);
    }
  }, [depths, depthFilter.length]);

  const toggleYear = (y) =>
    setActiveYears((curr) =>
      curr.includes(y) ? curr.filter((n) => n !== y) : [...curr, y]
    );

  if (isLoading)
    return (
      <Box
        height={height}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <CircularProgress />
      </Box>
    );

  if (isError || !series.length)
    return (
      <Box
        height={height}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        No data for current selections
      </Box>
    );

  return (
    <>
      <YearBarChart
        series={series}
        unit={unit}
        activeYears={activeYears}
        height={height}
      />

      <Box
        component="fieldset"
        mt={2}
        p={2}
        borderColor="grey.300"
        borderRadius={4}
        bgcolor="#f9f9f9"
      >
        <Typography component="legend" variant="subtitle2" gutterBottom>
          Additional Graph Filters
        </Typography>

        <Box
          display="flex"
          flexWrap="wrap"
          alignItems="flex-start"
          flexDirection="column"
          style={{ gap: 16 }}
        >
          {/* Multiselect Depths */}
          <Box display="flex" flexDirection="column" minWidth={200}>
            <Typography variant="caption" color="textSecondary" gutterBottom>
              Select Depths (m) to include
            </Typography>
            <FormControl size="small" fullWidth>
              <Select
                multiple
                value={depthFilter}
                onChange={(e) => setDepthFilter(e.target.value)}
                renderValue={(selected) => selected.join(", ")}
              >
                {depths.map((d) => (
                  <MenuItem key={d} value={d}>
                    <Checkbox checked={depthFilter.includes(d)} />
                    <ListItemText primary={d} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Year Chips */}
          {showYearSelector && (
            <Box display="flex" flexDirection="column">
              <Typography variant="caption" color="textSecondary" gutterBottom>
                Select Years to Compare
              </Typography>
              <Box display="flex" flexWrap="wrap" style={{ gap: 2 }}>
                {years.map((y) => (
                  <Chip
                    key={y}
                    label={y}
                    clickable
                    color={activeYears.includes(y) ? "primary" : "default"}
                    onClick={() => toggleYear(y)}
                    style={{ width: 60 }}
                    role="checkbox"
                    aria-checked={activeYears.includes(y)}
                    aria-label={`Toggle year ${y}`}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
}
